import React, { useEffect, useState } from "react";
import { responseHero } from "../../../../utils/axiosInterceptor";
import { toast } from "react-toastify";
import { Group } from "../../../../models/group.model";
import { DaisyDeposits } from "../../../../models/daisydeposits.model";
import { UserWithInvestment } from "../../../../types/userWithInvestment";
import { getGroupMembers } from "../../../../api/getGroupMembers";
import { System } from "../../../../models/system.model";

type ModalProps = {
    title: string;
    showModal: boolean;
    closeModal: () => void;
    handleStateChange: () => void;
    system: System | undefined;
    row: DaisyDeposits | undefined;
};

const ModifyDepositModal = ({ title, showModal, closeModal, handleStateChange, system, row }: ModalProps) => {
    const [recordData, setRecordData] = useState<DaisyDeposits>({
        id: -1,
        date: new Date(),
        deposit: 0,
        user_id: -1,
        group_id: -1,
        user: null,
        group: null,
        toGroupId: null,
        fromGroupId: null,
        toGroup: null,
        fromGroup: null,
        relevance: false
    });

    const [groups, setGroups] = useState<Group[]>([]);
    const [groupMembers, setGroupMembers] = useState<UserWithInvestment[]>([]);

    useEffect(() => {
        const getGroups = async () => {
            try {
                const response = await responseHero.get(`${process.env.REACT_APP_API_URL}/groups`);
                const filteredGroups = response.data.filter((group: Group) => group.system_id === system?.id && group.running);
                setGroups(filteredGroups);
            } catch (error: any) {
                console.error(error.response?.data);
            }
        };

        if (system) getGroups();
    }, [system]);

    useEffect(() => {
        if (!row) return;

        setRecordData({
            id: row.id,
            date: row.date,
            deposit: row.deposit,
            user_id: row.user_id,
            group_id: row.group_id,
            group: row.group,
            user: row.user,
            toGroupId: row.toGroupId,
            fromGroupId: row.fromGroupId,
            toGroup: row.toGroup,
            fromGroup: row.fromGroup,
            relevance: row.relevance
        });

        if (row.group?.group_name) {
            handleGroupSelection(row.group.group_name);
        }
    }, [row]);

    useEffect(() => {
        if (
            row?.user?.name &&
            groupMembers.length > 0 &&
            recordData.group_id === row.group_id
        ) {
            handleUserSelection(row.user.name, groupMembers);
        }
    }, [groupMembers, row, recordData.group_id]);
    

    const handleChange = (type: keyof DaisyDeposits, e: React.ChangeEvent<HTMLInputElement>) => {
        setRecordData(prev => ({
            ...prev,
            [type]: type === "date" ? e.target.value : parseInt(e.target.value, 10)
        }));
    };

    const handleGroupSelection = (group_name: string) => {
        const selectedGroup = groups.find(group => group.group_name === group_name);
        if (!selectedGroup) return;

        setRecordData(prev => ({
            ...prev,
            group_id: selectedGroup.group_id
        }));

        getGroupMembers(selectedGroup.group_id)
            .then(members => {
                setGroupMembers(members);
            })
            .catch(console.error);
    };

    const handleUserSelection = (user_name: string, members: UserWithInvestment[]) => {
        const user = members.find(member => member.name === user_name);
        if (!user) return;

        setRecordData(prev => ({
            ...prev,
            deposit: user.invested,
            user_id: user.user_id
        }));
    };

    const updateRecord = async () => {
        try {
            if(!row) return;
            
            await responseHero.put(`${process.env.REACT_APP_API_URL}/daisydeposit/${row.id}`, { record: recordData });
            closeModal();
            handleStateChange();
            toast.success(`[Befizetés]: Sikeresen létrehoztad a rekordot!`);
        } catch (error) {
            closeModal();
            toast.error(`Sikertelen adatrögzítés! Próbáld meg újra kiválasztani a csoportot és az ügyfelet!`);
        }
    };

    return (
        <>
            {showModal && (
                <>
                    <div className="fixed inset-0 z-50 flex justify-center items-center">
                        <div className="relative w-full max-w-4xl mx-auto my-6">
                            <div className="bg-white border-0 rounded-lg shadow-lg relative flex flex-col w-full">
                                <div className="flex items-start justify-between p-5 border-b border-solid border-gray-300 rounded-t">
                                    <h3 className="text-xl font-semibold">
                                        <span className="text-brown">{system?.name}</span> - {title} | {recordData?.date?.toString().split('T')[0]}
                                    </h3>
                                </div>
                                <div className="p-6">
                                <form className="space-y-6">
                                    <div className="flex gap-4">
                                        {/* Dátum */}
                                        <div className="w-1/4">
                                        <label htmlFor="date" className="block text-sm font-bold text-brown mb-1">Dátum</label>
                                        <input
                                            id="date"
                                            type="date"
                                            value={recordData?.date?.toString().split('T')[0]}
                                            onChange={(e) => handleChange('date', e)}
                                            className="w-full border px-3 py-2 rounded text-gray-700"
                                        />
                                        </div>

                                        {/* Összeg */}
                                        <div className="w-1/4">
                                        <label htmlFor="deposit" className="block text-sm font-bold text-brown mb-1">Összeg (USD)</label>
                                        <input
                                            id="deposit"
                                            type="number"
                                            value={recordData.deposit}
                                            onChange={(e) => handleChange('deposit', e)}
                                            className="w-full border px-3 py-2 rounded text-gray-700"
                                            placeholder="USD"
                                        />
                                        </div>

                                        {/* Csoport */}
                                        <div className="w-1/4">
                                        <label htmlFor="groupSelect" className="block text-sm font-bold text-brown mb-1">Csoport</label>
                                        <select
                                            id="groupSelect"
                                            value={groups.find(g => g.group_id === recordData.group_id)?.group_name || "Nincs kiválasztva"}
                                            onChange={(e) => handleGroupSelection(e.target.value)}
                                            className="w-full bg-dark-blue text-white px-3 py-2 rounded"
                                        >
                                            <option disabled value="Nincs kiválasztva">Nincs kiválasztva</option>
                                            {groups.map(group => (
                                            <option key={group.group_id} value={group.group_name}>{group.group_name}</option>
                                            ))}
                                        </select>
                                        </div>

                                        {/* Ügyfél */}
                                        <div className="w-1/4">
                                        <label htmlFor="userSelect" className="block text-sm font-bold text-brown mb-1">Ügyfél</label>
                                        <select
                                            id="userSelect"
                                            value={groupMembers.find(u => u.user_id === recordData.user_id)?.name || "Nincs kiválasztva"}
                                            onChange={(e) => handleUserSelection(e.target.value, groupMembers)}
                                            className="w-full bg-dark-blue text-white px-3 py-2 rounded"
                                        >
                                            <option disabled value="Nincs kiválasztva">Nincs kiválasztva</option>
                                            {groupMembers.map(user => (
                                            <option key={user.user_id} value={user.name}>{user.name}</option>
                                            ))}
                                        </select>
                                        </div>
                                    </div>

                                    <div className="flex gap-4">
                                        {/* From Group */}
                                        <div className="w-1/2">
                                        <label htmlFor="fromGroupSelect" className="block text-sm font-bold text-brown mb-1">
                                            Ebből a csoportból lett áthelyezve
                                        </label>
                                        <select
                                            id="fromGroupSelect"
                                            value={recordData.fromGroupId ? groups.find(g => g.group_id === recordData.fromGroupId)?.group_name : "Banki beutalás"}
                                            onChange={(e) => {
                                            const name = e.target.value;
                                            const group = groups.find(g => g.group_name === name);
                                            setRecordData(prev => ({
                                                ...prev,
                                                fromGroupId: group ? group.group_id : null,
                                                fromGroup: group || null
                                            }));
                                            }}
                                            className="w-full bg-dark-blue text-white px-3 py-2 rounded"
                                        >
                                            <option value="Banki beutalás">Banki beutalás</option>
                                            {groups.map(group => (
                                            <option key={group.group_id} value={group.group_name}>{group.group_name}</option>
                                            ))}
                                        </select>
                                        </div>

                                        {/* To Group */}
                                        <div className="w-1/2">
                                        <label htmlFor="toGroupSelect" className="block text-sm font-bold text-brown mb-1">
                                            Ebbe a csoportba lett áthelyezve
                                        </label>
                                        <select
                                            id="toGroupSelect"
                                            value={recordData.toGroupId ? groups.find(g => g.group_id === recordData.toGroupId)?.group_name : "Banki beutalás"}
                                            onChange={(e) => {
                                            const name = e.target.value;
                                            const group = groups.find(g => g.group_name === name);
                                            setRecordData(prev => ({
                                                ...prev,
                                                toGroupId: group ? group.group_id : null,
                                                toGroup: group || null
                                            }));
                                            }}
                                            className="w-full bg-dark-blue text-white px-3 py-2 rounded"
                                        >
                                            <option value="Banki beutalás">Banki beutalás</option>
                                            {groups.map(group => (
                                            <option key={group.group_id} value={group.group_name}>{group.group_name}</option>
                                            ))}
                                        </select>
                                        </div>
                                    </div>
                                    </form>

                                </div>
                                <div className="flex justify-end p-6 border-t">
                                    <button onClick={closeModal} className="text-red-500 font-bold px-6 py-2 text-sm mr-2">Mégse</button>
                                    <button onClick={updateRecord} className="text-cream bg-dark-blue font-bold text-sm px-6 py-3 rounded">Módosítom</button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"></div>
                </>
            )}
        </>
    );
};

export default ModifyDepositModal;